import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Lock, CheckCircle2, Loader2 } from "lucide-react";

const STATUS_OPTIONS = [
  { code: "CALISIYOR", label: "Çalışıyor", color: "emerald" },
  { code: "IZINLI", label: "İzinli", color: "cyan" },
  { code: "RAPORLU", label: "Raporlu", color: "amber" },
  { code: "HAFTA_TATILI", label: "Hafta Tatili", color: "sky" },
  { code: "UCRETSIZ_IZIN", label: "Ücretsiz İzin", color: "purple" },
  { code: "GELMEDI", label: "Gelmedi", color: "rose" },
];

const COLORS = {
  emerald: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  cyan: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  amber: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  sky: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  purple: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  rose: "bg-rose-500/20 text-rose-300 border-rose-500/40",
};

export default function Puantaj() {
  const { user } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get("/personnel"),
        api.get("/puantaj/today"),
      ]);
      setPersonnel(pRes.data);
      // Default every person to CALISIYOR
      const defaults = {};
      pRes.data.forEach((p) => { defaults[p.id] = "CALISIYOR"; });
      setStatuses(defaults);
      // Find my dept's today puantaj
      const mine = tRes.data.find(
        (d) => d.hotel_code === user.hotel_code && d.department_code === user.department_code
      );
      if (mine) setExisting(mine);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = (pid, code) => setStatuses((prev) => ({ ...prev, [pid]: code }));

  const submit = async () => {
    setSubmitting(true);
    setMsg("");
    try {
      const entries = Object.entries(statuses).map(([personnel_id, status]) => ({ personnel_id, status }));
      await api.post("/puantaj/submit", { entries });
      setMsg("Bugünün puantaj kaydı başarıyla İK'ya iletildi ve kilitlendi ✔");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Hata oluştu");
    }
    setSubmitting(false);
  };

  const today = new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", weekday: "long" });

  if (loading) return <div className="text-center py-20 font-mono text-cyan-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Yükleniyor...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="glass-card hud-corners p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-xl font-bold text-cyan-300 tracking-widest uppercase text-glow-sm">
              Günlük Puantaj
            </h1>
            <div className="font-mono text-xs text-cyan-400/70 tracking-widest uppercase mt-1">
              {today}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-300">{user.name}</div>
            <div className="font-mono text-[10px] text-cyan-400/60 uppercase tracking-widest">
              {user.hotel_code} / {user.department_code}
            </div>
          </div>
        </div>
      </div>

      {existing ? (
        <div data-testid="puantaj-locked" className="glass-card hud-corners p-8 text-center">
          <Lock className="w-16 h-16 text-cyan-400 mx-auto mb-4 text-glow" />
          <h2 className="font-display text-xl font-bold text-cyan-300 tracking-widest uppercase mb-2 text-glow-sm">
            Puantaj Kilitlendi
          </h2>
          <p className="text-slate-300 mb-4">Bugünün kaydını tamamladınız; kayıt kilitlidir. Hatalı bir giriş için <a href="/duzeltme-talebi" data-testid="go-correction-link" className="text-cyan-300 underline">Düzeltme Talebi Oluşturun</a>.</p>
          <div className="text-xs font-mono text-slate-500 mb-6">
            Giriş zamanı: {new Date(existing.submitted_at).toLocaleString("tr-TR")}
          </div>
          <div className="space-y-2 max-w-2xl mx-auto text-left max-h-96 overflow-y-auto">
            {existing.entries.map((e) => {
              const s = STATUS_OPTIONS.find((o) => o.code === e.status);
              return (
                <div key={e.personnel_id} className="flex items-center justify-between p-2 rounded bg-slate-900/40 border border-cyan-500/15">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-cyan-400">#{e.sira_no}</span>
                    <div>
                      <div className="text-sm text-slate-200">{e.full_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{e.role}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${COLORS[s?.color || "cyan"]}`}>
                    {s?.label || e.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div className="glass-card p-3 mb-4 flex flex-wrap gap-2 justify-center">
            <span className="text-xs font-mono text-cyan-300/60 tracking-widest uppercase self-center mr-2">Varsayılan: Çalışıyor</span>
            {STATUS_OPTIONS.map((s) => (
              <span key={s.code} className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${COLORS[s.color]}`}>
                {s.label}
              </span>
            ))}
          </div>

          <div className="space-y-2">
            {personnel.map((p) => (
              <div key={p.id} data-testid={`puantaj-row-${p.id}`} className="glass-card p-3 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 min-w-[200px]">
                  <span className="font-mono text-sm font-bold text-cyan-400 w-10">#{p.sira_no}</span>
                  <div>
                    <div className="text-sm font-medium text-slate-100">{p.full_name}</div>
                    <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">{p.role}</div>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {STATUS_OPTIONS.map((s) => {
                    const active = statuses[p.id] === s.code;
                    return (
                      <button
                        key={s.code}
                        data-testid={`status-${p.id}-${s.code}`}
                        onClick={() => setStatus(p.id, s.code)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border transition ${
                          active ? COLORS[s.color] + " ring-2 ring-offset-1 ring-offset-slate-900 ring-current" : "border-slate-700 text-slate-500 hover:border-cyan-500/40"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="sticky bottom-4 mt-6">
            <button
              data-testid="submit-puantaj"
              onClick={submit}
              disabled={submitting}
              className="w-full py-4 btn-cyan rounded-xl font-display font-black text-lg tracking-widest uppercase disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Bugünün Puantajını Kaydet
            </button>
          </div>

          {msg && <div data-testid="puantaj-msg" className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-center font-mono text-sm">{msg}</div>}
        </>
      )}
    </div>
  );
}
